import './styles.css';
import { useParams } from 'react-router-dom';
import ItemCard from "../../components/ItemCard";
import { useEffect, useState, useRef } from "react";
import {
    fetchProductsByCategory,
    fetchNextCategoryPage,
    selectCategoryState
} from "../../features/products";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from 'react-infinite-scroll-component';
import { Skeleton, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ItemCardSkeleton from "../../components/ItemCardSkeleton";
import ProductsShowcase from "../../components/ProductsShowcase";
import { categoriesSelectors, fetchSubcategories } from "../../features/categories";
import { secondaryMenuSelectors } from "../../features/secondaryMenu";
import { geoSelectors } from "../../features/geolocation";


const ProductsCategory = () => {
    const dispatch = useDispatch();
    const { categId } = useParams();
    const [showScrollTop, setShowScrollTop] = useState(false);

    const {
        selectCategoriesById
    } = categoriesSelectors;
    const category = useSelector(state => selectCategoriesById(state))[categId];
    const secondaryMenuItems = useSelector(secondaryMenuSelectors.selectSecondaryMenuItems);

    const name = category?.name
        || secondaryMenuItems?.find(item => String(item.object_id) === String(categId))?.title
        || 'Категория';
    const description = category?.description || '';

    const subcategories = useSelector(state => categoriesSelectors.selectSubcategories(state, categId));
    const subcategoriesLoading = useSelector(state => categoriesSelectors.selectSubcategoriesLoading(state, categId));
    const hasSubcategories = subcategories && subcategories.length > 0;
    const regionCode = useSelector(geoSelectors.selectRegionCode);
    const prevRegionRef = useRef(regionCode);
    const fetchedKeyRef = useRef(null);

    const categoryState = useSelector(state => selectCategoryState(state, categId));

    const {
        items,
        isLoading,
        error,
        isEmpty,
        hasMore,
        canLoadMore,
        paginationInfo
    } = categoryState;

    useEffect(() => {
        dispatch(fetchSubcategories(categId));
    }, [dispatch, categId]);

    useEffect(() => {
        if (subcategories === undefined) return;

        const isRegionChange = prevRegionRef.current !== regionCode;
        prevRegionRef.current = regionCode;

        const fetchKey = hasSubcategories
            ? `subs:${subcategories.map(s => s.cat_id).join(',')}`
            : `cat:${categId}`;

        if (fetchKey === fetchedKeyRef.current && !isRegionChange) return;
        fetchedKeyRef.current = fetchKey;

        if (!hasSubcategories) {
            dispatch(fetchProductsByCategory(categId, { limit: 10, refresh: true }));
        } else {
            subcategories.forEach(sub => {
                dispatch(fetchProductsByCategory(sub.cat_id, { limit: 10, refresh: isRegionChange }));
            });
        }
    }, [dispatch, categId, subcategories, hasSubcategories, regionCode]);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.pageYOffset > 300);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const loadMoreProducts = () => {
        dispatch(fetchNextCategoryPage(categId));
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (subcategoriesLoading || subcategories === undefined) {
        return (
            <div className="product-page">
                <div style={{ marginBottom: 30 }}>
                    <Skeleton.Input active size="large" style={{ width: 300, height: 44, borderRadius: 10 }} />
                </div>
                {Array(3).fill(null).map((_, i) => (
                    <div key={i} style={{ marginBottom: 40 }}>
                        <Skeleton.Input active style={{ width: 180, height: 40, borderRadius: 10, marginBottom: 16 }} />
                        <div className="product-page-grid">
                            {Array(5).fill(null).map((_, j) => (
                                <ItemCardSkeleton key={j} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (hasSubcategories) {
        return (
            <div className="product-page" >
                <div className="showcase-header" style={{ marginBottom: '30px' }}>
                    <div className="showcase-title main">
                        {name}
                    </div>
                    {description && (
                        <div className="showcase-description">
                            {description}
                        </div>
                    )}
                </div>

                {subcategories.map((sub) => (
                    <ProductsShowcase
                        key={sub.cat_id}
                        categoryId={sub.cat_id}
                        title={sub.name}
                        titleStyle="subcategory"
                        skipFetch
                        infinite
                    />
                ))}

                {showScrollTop && (
                    <button
                        className="scroll-to-top"
                        onClick={scrollToTop}
                        aria-label="Прокрутить наверх"
                    >
                        ↑
                    </button>
                )}
            </div>
        );
    }

    if (isLoading && items.length === 0) {
        return (
            <div className="product-page" >
                <div style={{ marginBottom: 30 }}>
                    <Skeleton.Input active size="large" style={{ width: 250, height: 32 }} />
                </div>
                <div className="product-page-grid">
                    {Array(8).fill(null).map((_, i) => (
                        <ItemCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    if (error && items.length === 0) {
        return (
            <div className="product-page">
                <div className="product-page-error">
                    <div className="error-icon">⚠️</div>
                    <div>Ошибка загрузки: {error}</div>
                    <button
                        className="retry-button"
                        onClick={() => dispatch(fetchProductsByCategory(categId, { limit: 10, refresh: true }))}
                    >
                        Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    if (isEmpty) {
        return (
            <div className="product-page">
                <div className="product-page-empty">
                    <div className="empty-icon">📦</div>
                    <div>В этой категории пока нет товаров</div>
                </div>
            </div>
        );
    }

    return (
        <div className="product-page" >

            <div className="showcase-header" style={{marginBottom: '30px'}}>
                <div className="showcase-title main">
                    {name}
                </div>

                {description && (
                    <div className="showcase-description">
                        {description}
                    </div>
                )}

            </div>

            <InfiniteScroll
                dataLength={items.length}
                next={loadMoreProducts}
                hasMore={canLoadMore}
                endMessage={!hasMore &&
                    <div className="product-page-end">
                        ✓ Все товары загружены
                    </div>
                }
                className="product-page-scroll"
            >
                <div className="product-page-grid">
                    {items.map((product) => (
                        <ItemCard
                            key={product.id}
                            item={product}
                            {...product}
                        />
                    ))}
                </div>
                {isLoading && items.length > 0 && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Spin indicator={<LoadingOutlined spin style={{ color: '#DC0202', fontSize: 24 }} />} />
                    </div>
                )}
            </InfiniteScroll>

            {showScrollTop && (
                <button
                    className="scroll-to-top"
                    onClick={scrollToTop}
                    aria-label="Прокрутить наверх"
                >
                    ↑
                </button>
            )}
        </div>
    );
};

export default ProductsCategory;
